import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Form, Tab } from 'react-bootstrap';
import Tabs from 'react-bootstrap/Tabs';
import { MsgTransaction, Tag, Transaction } from 'types/rest/assetCatalogTypes';
import i18n from 'i18n';
import { MatcherDsl, MsgRequestDsl, MsgResponseDsl } from 'types/rest/dslTypes';
import { buildTransactionBody, getDefaultMessagingEmptyDsl } from 'utils/transactionUtils';
import { useDispatch } from 'react-redux';
import {
  bodyMatchersDropdownOperators,
  conditionalOperators,
  convertToBMIs,
  convertToHI,
  convertToMatcherDsls,
  convertToRMIs,
  createTransactionState,
  editorComponentsState,
  EditorTabType,
  handleTransactionChange,
  initialValidations,
  isTxnValid,
  MatcherKeys,
  onTabChanged,
  saveValidatedTransaction,
  tagSelectHandler,
  TransactionEditorProps,
  TransactionField,
  transactionMatchersDropdownOperators,
  updateTransactionState,
} from './TransactionEditor';
import MessagingRequestTab, { MessagingRequestTabState } from './RequestTab/MessagingRequestTab';
import MessagingResponseTab, { MessagingResponseTabState } from './ResponseTab/MessagingResponseTab';
import { DelayType, ResponseDelay } from '../../../../types/rest/mockServiceTypes';
import ProcessingActions from './ProcessingActions/ProcessingActions';
import TransactionCommonHeader from './TransactionCommonHeader';

export const allMatcherOperators: MatcherKeys = {
  ...transactionMatchersDropdownOperators,
  ...bodyMatchersDropdownOperators,
  ...conditionalOperators,
};

const createMessagingTxState = (transaction: Transaction): MsgTransaction => {
  return createTransactionState<MsgTransaction>(transaction, getDefaultMessagingEmptyDsl);
};

// eslint-disable-next-line react/display-name
const MessagingEditor = forwardRef((props: TransactionEditorProps, ref) => {
  const {
    tabRRState,
    setTabRRState,
    transactionState: msgTransactionState,
    setTransactionState: setMsgTransactionState,
    serviceId,
    setServiceId,
    serviceName,
    setServiceName,
    isDirty,
    setDirty,
    isValidating,
    setValidating,
    selectedResponseTab,
    setSelectedResponseTab,
    isSaveModalShown,
    setIsSaveModalShown,
    transactionName: messagingName,
    setTransactionName: setMessagingName,
  } = editorComponentsState(props, createMessagingTxState);

  // The component instance will be extended
  // with whatever you return from the callback passed
  // as the second argument
  useImperativeHandle(ref, () => ({
    saveTransactionHandler() {
      saveTransaction();
    },
  }));

  const dispatch = useDispatch();
  const saveTransaction = (selectedTab?: EditorTabType) => {
    initialValidations(
      setValidating,
      messagingName,
      msgRequestTabState,
      setTabRRState,
      props.onTabChanged,
      msgResponseTabState.isValid,
      false,
      dispatch,
    );

    //Save after validations
    if (isTxnValid(msgRequestTabState, msgResponseTabState, messagingName, serviceId)) {
      const requestDsl: MsgRequestDsl = {
        headers: convertToMatcherDsls(msgRequestTabState.headers),
        properties: convertToMatcherDsls(msgRequestTabState.properties),
        body: buildTransactionBody(msgRequestTabState.body),
      };

      const responseDsl: MsgResponseDsl = {
        messageType: msgResponseTabState.messageType,
        contentType: msgResponseTabState.contentType,
        content: msgResponseTabState.binary
          ? msgResponseTabState.content.toString()
          : Buffer.from(msgResponseTabState.content).toString('base64'),
        headers: msgResponseTabState.headers.map(item => ({
          name: item.name || '',
          value: item.value || '',
        })),
        properties: msgResponseTabState.properties.map(item => ({
          name: item.name || '',
          value: item.value || '',
          type: item.type,
        })),
        binary: msgResponseTabState.binary,
        charset: msgResponseTabState.charset,
        responseDelay: msgResponseTabState.responseDelay,
        failoverEnabled: msgResponseTabState.failoverEnabled,
      };

      const transaction: MsgTransaction = {
        type: 'MESSAGING',
        id: msgTransactionState.id,
        name: messagingName.value as string,
        dsl: {
          type: 'MESSAGING',
          priority: 1,
          requestDsl: requestDsl,
          responseDsl: responseDsl,
        },
        description: msgTransactionState.description,
        serviceName: serviceName,
        serviceId: serviceId,
        tags: msgTransactionState.tags,
        priority: msgTransactionState.priority,
      };

      saveValidatedTransaction(transaction, serviceId, selectedTab, setTabRRState, props.onSave, props.isCreate);
    }
  };

  const emptyContentFound = (matchers: MatcherDsl[]): boolean => {
    return matchers.some(matcher => !matcher.matchingValue);
  };

  const buildInitRequestTabState = (): MessagingRequestTabState => {
    const msgRequestDsl = msgTransactionState.dsl.requestDsl;
    const initialState: MessagingRequestTabState = {
      headers: convertToRMIs('matchers-header', msgRequestDsl.headers),
      body: msgRequestDsl.body ? convertToBMIs(msgRequestDsl.body) : [],
      properties: convertToRMIs('matchers-properties', msgRequestDsl.properties),
      legacySampleBody: (msgTransactionState.sampleBody as string) ?? null,
      valid: msgRequestDsl.headers.length > 0 || msgRequestDsl.properties.length > 0 || msgRequestDsl.body.length > 0,
      dirty: false,
      validate: null,
    };
    return initialState;
  };
  const [msgRequestTabState, setMsgRequestTabState] = useState<MessagingRequestTabState>(buildInitRequestTabState());

  const buildInitResponseTabState = (): MessagingResponseTabState => {
    const msgResponseDsl = { ...msgTransactionState.dsl.responseDsl };
    return {
      messageType: msgResponseDsl.messageType,
      content: msgResponseDsl.binary
        ? msgResponseDsl.content
          ? msgResponseDsl.content
          : ''
        : msgResponseDsl.content
        ? Buffer.from(msgResponseDsl.content, 'base64').toString()
        : '',
      headers: convertToHI(msgResponseDsl.headers),
      properties: convertToHI(msgResponseDsl.properties),
      charset: msgResponseDsl.charset,
      contentType: msgResponseDsl.contentType,
      responseDelay: msgResponseDsl.responseDelay
        ? msgResponseDsl.responseDelay
        : ({ type: DelayType.NONE } as ResponseDelay),
      failoverEnabled: msgResponseDsl.failoverEnabled,
      isValid: true,
      dirty: false,
      binary: msgResponseDsl.binary,
    };
  };
  const [msgResponseTabState, setMsgResponseTabState] = useState<MessagingResponseTabState>(
    buildInitResponseTabState(),
  );

  useEffect(() => {
    if (!isDirty) {
      setDirty(messagingName.dirty || msgRequestTabState.dirty || msgResponseTabState.dirty);
    }
    if (msgTransactionState.updated !== props.transaction.updated) {
      setMsgTransactionState(props.transaction as MsgTransaction);
    }
  }, [isDirty, messagingName.dirty, msgRequestTabState.dirty, msgResponseTabState.dirty, props.transaction]);

  return (
    <Form id="qa-transaction-editor" className={props.isEmbedded ? 'bg-white' : 'm-4'}>
      <TransactionCommonHeader
        setIsSaveModalShown={setIsSaveModalShown}
        saveTransaction={saveTransaction}
        saveModalShown={isSaveModalShown}
        editorProps={props}
        serviceId={serviceId}
        setServiceId={setServiceId}
        setServiceName={setServiceName}
        setDirty={setDirty}
        validating={isValidating}
        transactionState={msgTransactionState}
        tagSelectHandler={(tags: Tag[]) => {
          tagSelectHandler<MsgTransaction>(tags, setDirty, msgTransactionState, setMsgTransactionState);
        }}
        onChange={event =>
          handleTransactionChange(event, 'description', setDirty, msgTransactionState, setMsgTransactionState)
        }
        updateTransactionState={(field: TransactionField, value: any) =>
          updateTransactionState(field, value, setDirty, msgTransactionState, setMsgTransactionState)
        }
        updateNameInput={setMessagingName}
      />

      <Tabs
        activeKey={tabRRState}
        onSelect={(key: any) =>
          onTabChanged(key, setIsSaveModalShown, setTabRRState, props.onTabChanged, props.transaction.id)
        }
        className="mb-0"
      >
        <Tab id="qa-tab-request" title={i18n.t('transactions:editor.requestMatcher')} eventKey="request">
          <MessagingRequestTab
            isReadOnly={props.isReadOnly}
            state={msgRequestTabState}
            updateState={setMsgRequestTabState}
          />
        </Tab>
        <Tab id="qa-tab-response" title={i18n.t('transactions:editor.response')} eventKey="response">
          <MessagingResponseTab
            isReadOnly={props.isReadOnly}
            state={msgResponseTabState}
            isResponseBodyMagicStringInvalid={props.isResponseBodyMagicStringInvalid}
            isResponseHeaderMagicStringInvalid={props.isResponseHeaderMagicStringInvalid}
            updateState={setMsgResponseTabState}
            msgRequestTabState={msgRequestTabState}
            resHeaderInvalidMagicStrings={props.resHeaderInvalidMagicStrings}
            resBodyInvalidMagicStrings={props.resBodyInvalidMagicStrings}
            activeResponseTab={selectedResponseTab}
            activeResponseTabChanged={activeTab => {
              setSelectedResponseTab(activeTab);
            }}
            serviceId={props.transaction.serviceId}
          />
        </Tab>
        <Tab
          id="qa-tab-processing-actions"
          title={i18n.t('transactions:processingActions.title')}
          eventKey="processingActions"
        >
          <ProcessingActions
            transactionId={props.transaction.id ? props.transaction.id : 1}
            serviceId={serviceId}
            tableOptions={{
              isSelectable: true,
              isSimple: false,
              columns: {
                name: true,
                type: true,
                conditions: true,
                actions: true,
              },
            }}
          />
        </Tab>
      </Tabs>
    </Form>
  );
});

export default MessagingEditor;
